import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { LogSearchResultDto, MachineDto } from "../Models/LogSearchResultDto";
import { SlackSearchFiltersContext } from "./SlackSearchFiltersContext";
import { SearchLogDto } from "../Models/SearchLogDto";
import axios from "axios";
import { AOO_API_HOST } from "../../../env.js";
import { MachinesContext } from "../../../context/MachinesContext";

interface ISlackSearchResultsContext {
  searchedMachinesFiltered: LogSearchResultDto | null;
  fetchMachineLogs: (
    searchParams?: SearchLogDto,
    signal?: AbortSignal
  ) => Promise<void>;
  findMachineById: (machineId: string) => MachineDto | null;
  isLoading: boolean;
  error: Error | null;
  selectedInspector: any | null;
  setSelectedInspector: React.Dispatch<React.SetStateAction<any | null>>;
  hasMachineFlag: (machineId: string) => boolean;
  toggleMachineFlag: (machineId: string) => Promise<void>;
  flagGroupedMachines: {
    WithFlag: { [key: string]: MachineDto[] };
    WithoutFlag: { [key: string]: MachineDto[] };
  } | null;
}

export const SlackSearchResultsContext = createContext<
  ISlackSearchResultsContext | undefined
>(undefined);

export const SearchResultsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [searchedMachines, setSearchedMachines] =
    useState<LogSearchResultDto | null>(null);
  const [searchedMachinesFiltered, setSearchedMachinesFiltered] =
    useState<LogSearchResultDto | null>(null); // filtered without backend request

  const { composeSearchParameters } = useContext(SlackSearchFiltersContext);
  const { machineDtoMap } = useContext(MachinesContext);

  // Function to determine if a machine has a flag
  const hasMachineFlag = useCallback(
    (machineId: string): boolean => {
      if (!selectedInspector || !machineDtoMap[machineId]?.InspectorsId)
        return false;
      return machineDtoMap[machineId].InspectorsId.some(
        (x) => x.FullName === selectedInspector.FullName
      );
    },
    [selectedInspector, machineDtoMap]
  );

  // Function to toggle the flag for a machine
  const toggleMachineFlag = useCallback(
    async (machineId: string): Promise<void> => {
      if (!selectedInspector || !machineDtoMap[machineId]) return;

      try {
        const machine = machineDtoMap[machineId];
        let updatedInspectors = [...(machine.InspectorsId || [])];

        const inspectorIndex = updatedInspectors.findIndex(
          (inspector) => inspector.FullName === selectedInspector.FullName
        );

        if (inspectorIndex >= 0) {
          updatedInspectors.splice(inspectorIndex, 1);
        } else {
          updatedInspectors.push(selectedInspector);
        }

        const workersResponse = await axios.get(
          `${AOO_API_HOST}/aoo-api/Productions/get-workers`
        );
        const workers = workersResponse.data || [];

        const workerIds = updatedInspectors
          .map((inspector) => {
            const worker = workers.find(
              (w) => w.FullName === inspector.FullName
            );
            return worker?.WorkerId;
          })
          .filter((id) => id != null);

        await axios.post(
          `${AOO_API_HOST}/aoo-api/Productions/post-machine-inspector`,
          {
            MachinesId: [machineId],
            WorkerIds: workerIds,
          }
        );

        const updatedMachine = {
          ...machine,
          InspectorsId: updatedInspectors,
        };

        machineDtoMap[machineId] = updatedMachine;
      } catch (err) {
        console.error("Failed to update machine inspectors", err);
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to update machine inspectors")
        );
      }
    },
    [selectedInspector, machineDtoMap, setError]
  );

  // Group and sort machines when flag is active
  const flagGroupedMachines = useMemo(() => {
    if (!searchedMachinesFiltered || !selectedInspector) return null;

    // Collect all machines from all time periods
    const allMachines = [
      ...Object.values(searchedMachinesFiltered.YesterdayLogs).flat(),
      ...Object.values(searchedMachinesFiltered.LastWeekLogs).flat(),
      ...Object.values(searchedMachinesFiltered.LastMonthLogs).flat(),
      ...Object.values(searchedMachinesFiltered.PastLogs).flat(),
    ];

    // Group machines by flag status
    const withFlag: MachineDto[] = [];
    const withoutFlag: MachineDto[] = [];

    allMachines.forEach((machine) => {
      if (hasMachineFlag(machine.MachineId)) {
        withFlag.push(machine);
      } else {
        withoutFlag.push(machine);
      }
    });

    // Sort machines by machine code, then by machine name
    const sortMachines = (machines: MachineDto[]) => {
      return machines.sort((a, b) => {
        // First sort by machine code
        const codeComparison = a.MachineCode.localeCompare(b.MachineCode);
        if (codeComparison !== 0) return codeComparison;

        // If machine codes are the same, sort by machine name
        return a.MachineName.localeCompare(b.MachineName);
      });
    };

    return {
      WithFlag: { group: sortMachines(withFlag) },
      WithoutFlag: { group: sortMachines(withoutFlag) },
    };
  }, [searchedMachinesFiltered, selectedInspector, hasMachineFlag]);

  const fetchMachineLogs = useCallback(
    async (searchParams?: SearchLogDto, signal?: AbortSignal) => {
      setIsLoading(true);
      setError(null);

      try {
        // Use provided search parameters or compose from context
        const parameters = searchParams || composeSearchParameters();
        const response = await axios.post(
          `${AOO_API_HOST}/aoo-api/ProductionsLog/SearchLogs`,
          parameters,
          {
            signal,
          }
        );
        setSearchedMachines(response.data || null);
      } catch (err) {
        console.error("Failed to fetch logs", err);
        setError(
          err instanceof Error ? err : new Error("Failed to fetch logs")
        );
      } finally {
        setIsLoading(false);
      }
    },
    [composeSearchParameters]
  );

  const findMachineById = useCallback(
    (machineId: string): MachineDto | null => {
      if (!searchedMachines) return null;

      // Helper function to search in a specific time period
      const searchInPeriod = (logs: { [key: string]: MachineDto[] }) => {
        for (const dateGroup of Object.values(logs)) {
          const machine = dateGroup.find((m) => m.MachineId === machineId);
          if (machine) return machine;
        }
        return null;
      };

      // Search in all time periods
      return (
        searchInPeriod(searchedMachines.YesterdayLogs) ||
        searchInPeriod(searchedMachines.LastWeekLogs) ||
        searchInPeriod(searchedMachines.LastMonthLogs) ||
        searchInPeriod(searchedMachines.PastLogs) ||
        null
      );
    },
    [searchedMachines]
  );

  useEffect(() => {
    if (fetchMachineLogs) fetchMachineLogs();
  }, [fetchMachineLogs]);

  useEffect(() => {
    if (!searchedMachines) return;

    // When a flag is active, we don't filter the machines
    // This allows the flagGroupedMachines hook to categorize all machines
    setSearchedMachinesFiltered(searchedMachines);
  }, [searchedMachines]);

  return (
    <SlackSearchResultsContext.Provider
      value={{
        searchedMachinesFiltered,
        fetchMachineLogs,
        findMachineById,
        isLoading,
        error,
        selectedInspector,
        setSelectedInspector,
        hasMachineFlag,
        toggleMachineFlag,
        flagGroupedMachines,
      }}
    >
      {children}
    </SlackSearchResultsContext.Provider>
  );
};
